import { Controller } from "@hotwired/stimulus"

// Phase 3.1 Tier 3 — connection detail page.
//
// Owns the tabbed body (Overview / Sync log / Webhook inbox / Settings),
// the notes-save AJAX submit, the force-disconnect confirmation modal,
// and the per-row Retry button on failed sync log rows.
//
// Toast feedback rather than full-page reloads. Modal backdrop dismiss
// (click outside the panel closes the modal). No browser confirm()s.
export default class extends Controller {
  static targets = [
    "tab", "panel",
    "notesField", "notesSubmit",
    "disconnectModal",
    "filterForm",
    "markResolvedModal", "markResolvedForm", "markResolvedInput"
  ]
  static values = {
    editNotesUrl: String,
    retryUrlTemplate: String,
    disconnectUrl: String,
    reauthUrl: String,
    markResolvedUrlTemplate: String
  }

  connect() {
    this._activateInitialTab()
    this._showPendingFlash()
  }

  // ── Tabs ──────────────────────────────────────────────
  switchTab(event) {
    const target = event.currentTarget.dataset.tab
    this._activate(target)
    if (window.history && window.location.hash !== `#${target}`) {
      window.history.replaceState({}, "", `#${target}`)
    }
  }

  _activateInitialTab() {
    let target = (window.location.hash || "").replace("#", "")
    if (target === "sync-log") target = "sync_log"
    if (!this.tabTargets.some(t => t.dataset.tab === target)) {
      target = this.tabTargets[0]?.dataset.tab || "overview"
    }
    this._activate(target)
  }

  _activate(target) {
    this.tabTargets.forEach(t => {
      const active = t.dataset.tab === target
      t.classList.toggle("text-brand", active)
      t.classList.toggle("border-brand", active)
      t.classList.toggle("text-gray-500", !active)
      t.classList.toggle("border-transparent", !active)
    })
    this.panelTargets.forEach(p => {
      p.classList.toggle("hidden", p.dataset.tab !== target)
    })
  }

  // ── Notes ─────────────────────────────────────────────
  async saveNotes(event) {
    event.preventDefault()
    if (!this.hasNotesFieldTarget) return
    if (this.hasNotesSubmitTarget) {
      this.notesSubmitTarget.disabled = true
      this.notesSubmitTarget.classList.add("opacity-60", "cursor-wait")
    }

    try {
      const body = new FormData()
      body.append("integration_connection[notes]", this.notesFieldTarget.value || "")
      const res = await fetch(this.editNotesUrlValue, {
        method: "PATCH",
        body,
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": this._csrfToken(),
          "Accept": "application/json"
        }
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok !== false) {
        this._toast(data.message || "Notes saved.", "notice")
      } else {
        this._toast(data.message || "Couldn't save notes.", "alert")
      }
    } catch (e) {
      this._toast("Network error — try again.", "alert")
    } finally {
      if (this.hasNotesSubmitTarget) {
        this.notesSubmitTarget.disabled = false
        this.notesSubmitTarget.classList.remove("opacity-60", "cursor-wait")
      }
    }
  }

  // ── Force-disconnect modal ────────────────────────────
  openDisconnectModal() {
    this.disconnectModalTarget.classList.remove("hidden")
  }

  closeDisconnectModal() {
    this.disconnectModalTarget.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  async confirmDisconnect() {
    try {
      const res = await fetch(this.disconnectUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": this._csrfToken(),
          "Accept": "application/json"
        }
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) {
        this._flashAndReload(data.message || "Disconnected.", "notice")
      } else {
        this._toast(data.message || "Couldn't disconnect.", "alert")
        this.closeDisconnectModal()
      }
    } catch (e) {
      this._toast("Network error — try again.", "alert")
    }
  }

  openMarkResolvedModal(event) {
    if (!this.hasMarkResolvedModalTarget || !this.hasMarkResolvedFormTarget) return
    this._currentSyncLogId = event.currentTarget.dataset.syncLogId
    if (this.hasMarkResolvedInputTarget) this.markResolvedInputTarget.value = ""
    this.markResolvedModalTarget.classList.remove("hidden")
    setTimeout(() => this.hasMarkResolvedInputTarget && this.markResolvedInputTarget.focus(), 0)
  }

  closeMarkResolvedModal() {
    if (this.hasMarkResolvedModalTarget) this.markResolvedModalTarget.classList.add("hidden")
  }

  async submitMarkResolved(event) {
    event.preventDefault()
    if (!this._currentSyncLogId || !this.markResolvedUrlTemplateValue) return
    const externalId = (this.markResolvedInputTarget?.value || "").trim()
    if (!externalId) {
      this._toast("Enter the QuickBooks ID first.", "alert")
      return
    }
    const url = this.markResolvedUrlTemplateValue.replace("SYNC_LOG_ID", this._currentSyncLogId)
    try {
      const body = new FormData()
      body.append("external_id", externalId)
      const res = await fetch(url, {
        method: "POST",
        body,
        credentials: "same-origin",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" }
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) {
        this.closeMarkResolvedModal()
        this._flashAndReload(data.message || "Marked.", "notice")
      } else {
        this._toast(data.message || "Couldn't mark.", "alert")
      }
    } catch (e) {
      this._toast("Network error — try again.", "alert")
    }
  }

  // ── Filters submit (server-rendered via GET) ─────────
  applyFilters() {
    // Native form submit is fine; this hook fires the Mixpanel event
    // before the browser navigates so we capture filter intent even on
    // the round-trip.
    if (window.mixpanel?.track) {
      const filters = {}
      new FormData(this.filterFormTarget).forEach((value, key) => {
        if (value) filters[key] = value
      })
      try {
        window.mixpanel.track("Integration Sync Log Filtered",
          { surface: "connection_detail", filters_applied: filters })
      } catch (_) {}
    }
  }

  // ── Helpers ───────────────────────────────────────────
  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  _toast(message, type) {
    const div = document.createElement("div")
    div.className = type === "notice" ? "flash-toast-notice" : "flash-toast-alert"
    div.dataset.controller = "flash"
    div.dataset.flashDismissAfterValue = "5000"
    div.textContent = message
    document.body.prepend(div)
  }

  _flashAndReload(message, type) {
    try {
      sessionStorage.setItem("connection-detail.flash",
        JSON.stringify({ message, type, ts: Date.now() }))
    } catch (_) {}
    window.location.reload()
  }

  _showPendingFlash() {
    try {
      const raw = sessionStorage.getItem("connection-detail.flash")
      if (!raw) return
      sessionStorage.removeItem("connection-detail.flash")
      const { message, type, ts } = JSON.parse(raw)
      // Stale flashes get dropped — a tab open >30s isn't the user's
      // round-trip from the disconnect button.
      if (!message || (Date.now() - (ts || 0)) > 30_000) return
      this._toast(message, type || "notice")
    } catch (_) {}
  }
}
